import React, { useEffect, useState } from "react";
import { RevoltIcon, RevoltModal, RevoltTabs } from "../../../components";
import "./LocalModelInfoModal.css";

function formatMetricSlashPair(left, right) {
  const l =
    left != null && left !== "" && !Number.isNaN(Number(left))
      ? Number(left).toLocaleString()
      : null;
  const r =
    right != null && right !== "" && !Number.isNaN(Number(right))
      ? Number(right).toLocaleString()
      : null;

  if (l != null && r != null) return `${l} / ${r}`;
  if (l != null) return l;
  if (r != null) return r;
  return "—";
}

export function LocalModelInfoModal({
  isOpen,
  onClose,
  metrics,
  flashAttentionEnabled,
  contextSizeMode,
  contextSizeMin,
  contextSizeMax,
  allocationMode,
  allocationSize,
}) {
  const [activeTab, setActiveTab] = useState("vram");

  useEffect(() => {
    if (isOpen) setActiveTab("vram");
  }, [isOpen]);

  const contextPair = formatMetricSlashPair(
    metrics?.runtime,
    metrics?.train
  );
  const gpuLayersPair = formatMetricSlashPair(
    metrics?.gpuLayers,
    metrics?.totalLayers
  );

  const allocationLabel =
    allocationMode === "manual" && allocationSize
      ? Number(allocationSize).toLocaleString()
      : allocationMode === "auto"
        ? "Auto"
        : "—";

  const contextLabel =
    contextSizeMode === "manual"
      ? contextSizeMin && contextSizeMax
        ? `${contextSizeMin} - ${contextSizeMax}`
        : "—"
      : "Auto";

  const tabs = [
    {
      id: "vram",
      label: "VRAM & First Use",
      content: (
        <div className="local-model-info-modal__body">
          <div className="local-model-info-modal__section">
            <h3 className="local-model-info-modal__section-title">How local models run</h3>
            <p className="local-model-info-modal__p">
              Local models run on your own machine. They use your GPU’s
              VRAM (video memory) to store the model and work buffers.
            </p>
            <p className="local-model-info-modal__p">
              If you have multiple GPUs, Revolt can spread the work across
              them (even when the GPUs aren’t identical). That can increase
              available capacity for larger context sizes.
            </p>
          </div>

          <div className="local-model-info-modal__section">
            <h3 className="local-model-info-modal__section-title">“Loading” vs “Ready to generate”</h3>
            <p className="local-model-info-modal__p">
              After you load a model, the first time you use it may take a
              while before it starts generating. This is expected initialization
              work in the local runtime (llama.cpp).
            </p>
            <p className="local-model-info-modal__p">
              If the first generation seems unusually slow, or it doesn’t respond
              at all after you wait a bit, restart the application and try again.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "context",
      label: "Context & GPU Layers",
      content: (
        <div className="local-model-info-modal__body">
          <div className="local-model-info-modal__section">
            <h3 className="local-model-info-modal__section-title">What these numbers mean</h3>
            <div className="local-model-info-modal__metric-grid">
              <div className="local-model-info-modal__metric">
                <div className="local-model-info-modal__metric-label">Context (runtime / trained)</div>
                <div className="local-model-info-modal__metric-value">{contextPair}</div>
              </div>
              <div className="local-model-info-modal__metric">
                <div className="local-model-info-modal__metric-label">GPU layers (VRAM / total)</div>
                <div className="local-model-info-modal__metric-value">{gpuLayersPair}</div>
              </div>
            </div>

            <div className="local-model-info-modal__spacer" />

            <p className="local-model-info-modal__p">
              Context controls how much of your conversation the model can keep in memory at once.
              If the <strong>runtime context</strong> is close to the model’s <strong>trained context</strong>,
              you’ll get the full experience (more prompts before you run out of room).
            </p>

            <p className="local-model-info-modal__p">
              If the runtime context is very small (for example, under <strong>4,096</strong>), you may only
              fit one or two prompts before the model can’t fit more. In that case, use the
              {" "}
              <span
                className="local-model-info-modal__more-visual"
                title="Local model options (More menu)"
                aria-hidden
              >
                <RevoltIcon name="lucide:more-horizontal" size={14} />
              </span>{" "}
              <strong>more menu</strong> button next to the <strong>load and unload</strong> button. Adjust the settings below.
            </p>
          </div>

          <div className="local-model-info-modal__section">
            <h3 className="local-model-info-modal__section-title">More settings: Flash attention, Context, Allocation</h3>
            <ul className="local-model-info-modal__list">
              <li>
                <strong>Flash attention:</strong> an optimized loading path when supported. It can improve
                how much context you can fit for the same amount of memory. Enabled by default.
              </li>
              <li>
                <strong>Context:</strong> the minimum and maximum context values you want available at startup
                (<code>min</code> / <code>max</code>). Typically the max is set to the allocation size.
              </li>
              <li>
                <strong>Allocation (most important):</strong> how much capacity it tries to reserve for the context.
                If you’re running out of room in a conversation since its set low automatically, set `<code>max</code>` close to the allocation size.
                A good starting point is typically <strong>8,192-16,384</strong>.
                Auto mode leaves this up to the estimation loading system. Good most of the time but not always. Tune this if a certain model is having below expected results.
              </li>
            </ul>

            <div className="local-model-info-modal__inline-note">
              {flashAttentionEnabled != null ? (
                <>
                  Flash attention:{" "}
                  <strong>{flashAttentionEnabled ? "enabled" : "disabled"}</strong>
                </>
              ) : (
                <>Flash attention: <strong>—</strong></>
              )}
              {" "}
              {"•"} {" "}
              Context: <strong>{contextLabel}</strong>
              {" "}
              {"•"} {" "}
              Allocation:{" "}
              <strong>
                {allocationMode === "manual" ? allocationLabel : "Auto"}
              </strong>
              {allocationMode === "manual" ? " tokens reserved" : ""}
            </div>
          </div>

          <div className="local-model-info-modal__section">
            <h3 className="local-model-info-modal__section-title">GPU layers: speed vs CPU fallback</h3>
            <p className="local-model-info-modal__p">
              GPU layers is the number of model layers placed into VRAM. If GPU layers equals total layers,
              everything runs on the GPU and you’ll usually see the best performance.
            </p>
            <p className="local-model-info-modal__p">
              If the values don’t match, some layers are offloaded to CPU. That creates a noticeable performance
              hit, especially during generation.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Local Models: Quick Info"
      containerClassName="local-model-info-modal-container"
      headerRightElement={
        <span className="local-model-info-modal__header-icon" aria-hidden>
          <RevoltIcon name="cpu" size={18} />
        </span>
      }
    >
      <RevoltTabs
        tabs={tabs}
        defaultTab="vram"
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="local-model-info-modal__tabs"
      />
    </RevoltModal>
  );
}

